/**
 * 詳細ページ(固有 URL の全画面)。
 *
 * ヘッダー(戻る / アバター / タイトル + 状態タグ / サブ行 / 主要アクション)
 * + サマリー帯(`SummaryStrip`、4〜5 枚)+ タブ。タブは `?tab=` に載せる
 * (履歴は増やさない。戻るで一覧へ帰れるように)。
 *
 * 本文は antd Tabs の `items[].children` として描く(タブの `aria-controls` が
 * 本文を指すように)。開いたタブから順に mount され、以後は残る(antd の既定)。
 * `lazy: true` のタブは**開いている間だけ** mount する(ポーリングを持つ MDM タブなど)。
 */
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Tabs } from "antd";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { SummaryStrip, type SummaryItem } from "./SummaryStrip";
import { useSearchParam } from "./urlState";

export type DetailTab = {
  key: string;
  label: string;
  /** タブ名の横の数(0 や未定義なら出さない) */
  badge?: number;
  children: ReactNode;
  hidden?: boolean;
  /** 押せない(権限が無い・前提が無いとき)。`title` に理由 */
  disabled?: boolean | string;
  /** 開いている間だけ mount する */
  lazy?: boolean;
};

export function DetailPage({
  back,
  avatar,
  title,
  tags,
  titleExtra,
  sub,
  actions,
  alert,
  summary,
  summaryLoading,
  tabs,
}: {
  back: { to: string; label: string };
  avatar?: ReactNode;
  title: ReactNode;
  /** タイトルの右の状態タグ */
  tags?: ReactNode;
  /** タイトル行の下(リンク列など。sub より先) */
  titleExtra?: ReactNode;
  /** サブ行(メール、所属、リンクなど。要素は横に並ぶ) */
  sub?: ReactNode;
  actions?: ReactNode;
  /** ヘッダー直下の注意(Alert) */
  alert?: ReactNode;
  summary?: SummaryItem[];
  summaryLoading?: boolean;
  tabs: DetailTab[];
}) {
  const navigate = useNavigate();
  const [tab, setTab] = useSearchParam("tab");
  const visible = tabs.filter((t) => !t.hidden);
  const active = visible.find((t) => t.key === tab && !t.disabled) ?? visible.find((t) => !t.disabled);

  return (
    <div className="ui-detail">
      <div className="ui-detail-head">
        <div className="ui-detail-top">
          <button
            type="button"
            className="ui-detail-back"
            aria-label={back.label}
            title={back.label}
            onClick={() => navigate(back.to)}
          >
            <ArrowLeftOutlined />
          </button>
          {avatar}
          <div className="ui-detail-title">
            <h1>
              <span>{title}</span>
              {tags}
            </h1>
            {titleExtra && <div className="ui-detail-title-extra">{titleExtra}</div>}
            {sub && <div className="ui-detail-sub">{sub}</div>}
          </div>
          {actions && <div className="ui-detail-actions">{actions}</div>}
        </div>
        {alert && <div className="ui-detail-alert">{alert}</div>}
        {summary && summary.length > 0 && <SummaryStrip items={summary} loading={summaryLoading} />}
      </div>
      <Tabs
        className="ui-detail-tabs"
        activeKey={active?.key}
        onChange={(k) => setTab(k, true)}
        items={visible.map((t) => ({
          key: t.key,
          disabled: !!t.disabled,
          label: (
            <span title={typeof t.disabled === "string" ? t.disabled : undefined}>
              <span>{t.label}</span>
              {t.badge ? <span className="ui-tab-badge">{t.badge}</span> : null}
            </span>
          ),
          children: (
            <div className="ui-detail-body">{t.lazy && active?.key !== t.key ? null : t.children}</div>
          ),
        }))}
      />
    </div>
  );
}
